import React from 'react';
import { twMerge } from 'tailwind-merge';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type McEntry = {
  mac: string;
  vid: number;
  mgmtPort: boolean;
  ports: string[];
  description: string;
};
type VlanBridge = { vlan_id: number };
type VlanCfg = Record<
  string,
  { vlan_map?: Record<string, 'tagged' | 'untagged'> }
>;

function sortPorts(ports: string[]): string[] {
  return ports.slice().sort((a, b) => {
    const na = Number((a.match(/\d+/) ?? ['9999'])[0]);
    const nb = Number((b.match(/\d+/) ?? ['9999'])[0]);
    if (na !== nb) return na - nb;
    return a.localeCompare(b);
  });
}

function isPortAllowed(vlanCfg: VlanCfg, port: string, vid: number): boolean {
  if (vid === 0) return true;
  return Object.keys(vlanCfg[port]?.vlan_map ?? {}).includes(String(vid));
}

export default function WebUiSettingsMulticastPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [snooping, setSnooping] = React.useState(true);
  const [entries, setEntries] = React.useState<McEntry[]>([]);
  const [bridges, setBridges] = React.useState<VlanBridge[]>([]);
  const [vlanCfg, setVlanCfg] = React.useState<VlanCfg>({});
  const [ports, setPorts] = React.useState<string[]>([]);
  const [newEntry, setNewEntry] = React.useState<McEntry>({
    mac: '',
    vid: 0,
    mgmtPort: false,
    ports: [],
    description: '',
  });
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [snoop, data, cfg, b] = await Promise.all([
      postWebUiApi<{ snooping?: boolean; enabled?: boolean }>({
        service: 'multicast',
        access: 'get',
        command: 'igmp snooping',
      }).catch(() => ({ snooping: true })),
      postWebUiApi<McEntry[]>({
        service: 'staticmc',
        access: 'get',
        command: 'get entries',
      }),
      postWebUiApi<VlanCfg>({
        service: 'vlan',
        access: 'get',
        command: 'vlan port settings',
      }),
      postWebUiApi<VlanBridge[]>({
        service: 'vlan',
        access: 'get',
        command: 'get bridges',
      }),
    ]);
    setSnooping(Boolean(snoop.snooping ?? snoop.enabled));
    setEntries(data ?? []);
    setVlanCfg(cfg ?? {});
    setBridges((b ?? []).sort((x, y) => x.vlan_id - y.vlan_id));
    setPorts(sortPorts(Object.keys(cfg ?? {})));
    setNewEntry((prev) => ({ ...prev, vid: (b?.[0]?.vlan_id ?? 0) as number }));
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const checkedExist = (entry: McEntry): boolean =>
    entry.mgmtPort || (entry.ports?.length ?? 0) > 0;

  const updateEntry = React.useCallback(
    async (entry: McEntry) => {
      try {
        await postWebUiApi({
          service: 'staticmc',
          access: 'set',
          command: 'add entry',
          parameters: entry,
        });
        setMessage('Entry updated.');
        setError(null);
        await reload();
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [reload],
  );

  return (
    <WebUiPageWrapper titleList={['Settings', 'Multicasts']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">Settings</h4>
        <table className="webui-table max-w-max">
          <thead>
            <tr className="webui-table-row-head">
              <th>Option</th>
              <th>Setting</th>
            </tr>
          </thead>
          <tbody>
            <tr className="webui-table-row-content">
              <td>Multicast Handling</td>
              <td>
                <WebUiSelect
                  inputSize="sm"
                  options={[
                    { label: 'IGMP Snooping', value: 'true' },
                    { label: 'Static Multicasts', value: 'false' },
                  ]}
                  disabled={readonly}
                  value={String(snooping)}
                  onChange={(e) => setSnooping(e.target.value === 'true')}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <WebUiButton
            type="button"
            disabled={readonly}
            onClick={async () => {
              try {
                await postWebUiApi({
                  service: 'multicast',
                  access: 'set',
                  command: 'igmp snooping',
                  parameters: { snooping },
                });
                setMessage('Multicast handling updated.');
                setError(null);
                await reload();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            Update Multicast Handling
          </WebUiButton>
        </div>
      </div>
      {!snooping ? (
        <div className="flex w-full flex-col gap-2">
          <h4 className="font-bold">Static Multicasts</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Multicast MAC Address</th>
                <th>VLAN ID</th>
                <th>MGMT Port</th>
                {ports.map((p) => (
                  <th key={`h-${p}`}>{p}</th>
                ))}
                <th>Description</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly}
                    value={newEntry.mac}
                    onChange={(e) =>
                      setNewEntry((p) => ({ ...p, mac: e.target.value }))
                    }
                  />
                </td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={bridges.map((b) => ({
                      label: b.vlan_id.toString(),
                      value: b.vlan_id.toString(),
                    }))}
                    disabled={readonly}
                    value={newEntry.vid}
                    onChange={(e) =>
                      setNewEntry((p) => ({
                        ...p,
                        vid: Number(e.target.value),
                        ports: [],
                      }))
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiInput
                    type="checkbox"
                    disabled={readonly}
                    checked={newEntry.mgmtPort}
                    onChange={(e) =>
                      setNewEntry((p) => ({ ...p, mgmtPort: e.target.checked }))
                    }
                  />
                </td>
                {ports.map((p) => {
                  const allowed = isPortAllowed(vlanCfg, p, newEntry.vid);
                  return (
                    <td
                      key={`new-${p}`}
                      className={twMerge(
                        'text-center',
                        !allowed && 'port_down',
                      )}
                    >
                      <WebUiInput
                        inputSize="sm"
                        type="checkbox"
                        disabled={readonly || !allowed}
                        checked={newEntry.ports.includes(p)}
                        onChange={(e) =>
                          setNewEntry((prev) => ({
                            ...prev,
                            ports: e.target.checked
                              ? [...prev.ports, p]
                              : prev.ports.filter((x) => x !== p),
                          }))
                        }
                      />
                    </td>
                  );
                })}
                <td>
                  <WebUiInput
                    inputSize="sm"
                    className="w-full"
                    disabled={readonly}
                    value={newEntry.description ?? ''}
                    onChange={(e) =>
                      setNewEntry((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </td>
                <td>
                  <WebUiButton
                    type="button"
                    disabled={readonly || !checkedExist(newEntry)}
                    onClick={async () => {
                      try {
                        await postWebUiApi({
                          service: 'staticmc',
                          access: 'set',
                          command: 'add entry',
                          parameters: newEntry,
                        });
                        setMessage('Entry added.');
                        setError(null);
                        await reload();
                      } catch (err) {
                        setError((err as Error).message);
                      }
                    }}
                  >
                    Add
                  </WebUiButton>
                </td>
              </tr>
              {entries.map((entry, idx) => (
                <tr
                  key={`${entry.mac}-${entry.vid}-${idx}`}
                  className="webui-table-row-content"
                >
                  <td className="text-center">{entry.mac}</td>
                  <td className="text-center">{entry.vid}</td>
                  <td className="text-center">
                    <WebUiInput
                      inputSize="sm"
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(entry.mgmtPort)}
                      onChange={(e) =>
                        setEntries((prev) =>
                          prev.map((it, i) =>
                            i === idx
                              ? { ...it, mgmtPort: e.target.checked }
                              : it,
                          ),
                        )
                      }
                    />
                  </td>
                  {ports.map((p) => {
                    const allowed = isPortAllowed(vlanCfg, p, entry.vid);
                    return (
                      <td
                        key={`${idx}-${p}`}
                        className={twMerge(
                          'text-center',
                          !allowed && 'port_down',
                        )}
                      >
                        <WebUiInput
                          inputSize="sm"
                          type="checkbox"
                          disabled={readonly || !allowed}
                          checked={entry.ports.includes(p)}
                          onChange={(e) =>
                            setEntries((prev) =>
                              prev.map((it, i) =>
                                i === idx
                                  ? {
                                      ...it,
                                      ports: e.target.checked
                                        ? [...it.ports, p]
                                        : it.ports.filter((x) => x !== p),
                                    }
                                  : it,
                              ),
                            )
                          }
                        />
                      </td>
                    );
                  })}
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      className="w-full"
                      disabled={readonly}
                      value={entry.description ?? ''}
                      onChange={(e) =>
                        setEntries((prev) =>
                          prev.map((it, i) =>
                            i === idx
                              ? { ...it, description: e.target.value }
                              : it,
                          ),
                        )
                      }
                    />
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <WebUiButton
                        type="button"
                        disabled={readonly || !checkedExist(entry)}
                        onClick={() => updateEntry(entry)}
                      >
                        Update
                      </WebUiButton>
                      <WebUiButton
                        type="button"
                        disabled={readonly}
                        onClick={async () => {
                          try {
                            await postWebUiApi({
                              service: 'staticmc',
                              access: 'set',
                              command: 'remove entry',
                              parameters: { mac: entry.mac, vid: entry.vid },
                            });
                            setMessage('Entry removed.');
                            setError(null);
                            await reload();
                          } catch (err) {
                            setError((err as Error).message);
                          }
                        }}
                      >
                        Remove
                      </WebUiButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </WebUiPageWrapper>
  );
}
